/**
 * **FI-36 (PR-A2 Step 5b)** — Helper SSOT per il calcolo del pool spell
 * visualizzato dal `<SpellPickPanel>` partendo da un `ActiveSpellGrant`.
 *
 * Wrapper sopra {@link getSpellPoolForGrant} che incapsula 2 aspetti
 * "data-aware del PG" che il service base non conosce:
 *
 *  1. **Linea Divina (LIDV)**: la grant `grantsSpells` su
 *     `chierico.lanciare-incantesimi-divini` ha `source.kind = 'own-class'`,
 *     ma il `ownClassId` derivato dal prefix `chierico.*` è errato per
 *     Druido / Paladino-Sacro (riusano LIDV via bundle cross-class). Il
 *     pool effettivo dipende dalla **mossa-padre** del PG
 *     (`chierico.divinita` → chierico-tradizione, `druido.divinita-della-natura`
 *     → druido, `paladino.ordine-sacro` → paladino-tradizione) — esattamente
 *     la discriminazione già implementata da `getActiveSpellLists`.
 *
 *  2. **Filtro `requiresArchetype`**: gli spell con `SpellAccess.requiresArchetype`
 *     (Voce della Terra ↔ Nano per le Tradizioni Negromanzia/Valore;
 *     tutta la Tradizione Natura del Paladino ↔ Elfo) devono essere
 *     filtrati fuori dal pool quando l'archetipo del PG non matcha.
 *
 * Per il path generic (Mago LIA / Corpus Arcano / Warlock LIDP / Magnus /
 * Tomo del Sapere / Rivelazioni Proibite) deferisce a
 * {@link getSpellPoolForGrant} con `ownClassId` derivato dal prefix moveId.
 */

import { dwrClassesById } from '../data/classes';
import { findSpellById, findSpellsByClass, findSpellsByClassAndTradition } from '../data/spells';
import type { MoveRef } from '../schemas/character';
import type { ClassId } from '../schemas/classId';
import {
  isAutoSpellGrantLevelRule,
  type SpellGrant,
  type SpellGrantLevelRule,
} from '../schemas/move';
import type { Spell, SpellLevel } from '../schemas/spell';
import {
  CHIERICO_CASTING_MOVE,
  getActiveSpellLists,
  isSpellAccessAvailable,
  type ActiveSpellList,
} from './spellAccess';
import { getSpellPoolForGrant, type ActiveSpellGrant } from './spellGrants';

/**
 * Contesto del PG necessario per calcolare il pool visualizzato di una
 * `ActiveSpellGrant`.
 */
export interface SpellPoolContext {
  /**
   * Mosse correnti del PG. L'helper espande internamente i bundle
   * (idempotente post-finalize) per coprire l'editing builder L1.
   */
  moves: readonly MoveRef[];
  /** ClassId PG, usato come fallback se il prefix del `moveId` non identifica una classe. */
  classId: ClassId;
  /** Archetipo PG (opzionale). Default: filtro `requiresArchetype` non applicato. */
  archetypeId?: string | undefined;
  /**
   * Livello "caster" effettivo per le regole `lte-character-level` /
   * `any-with-cap`. Per il builder L1 = 1. Per il level-up: livello da raggiungere
   * (o `effectiveCasterLevel` se la grant è su classe multiclass — FI-57).
   */
  characterLevel?: number;
  /** Spell ID già posseduti, esclusi dal pool. Default: nessuno. */
  alreadyKnownSpellIds?: ReadonlySet<string>;
}

/**
 * Calcola il pool di {@link Spell} visualizzati dal `<SpellPickPanel>` per
 * una specifica grant, applicando i filtri "data-aware del PG"
 * (discriminazione linea Divina + `requiresArchetype`) sopra ai filtri
 * `levelRule` / `source` di {@link getSpellPoolForGrant}.
 *
 * Per le grant "auto" (`all-of-level` / `fixed-list`) ritorna il pool
 * completo che la grant assegna automaticamente al PG — il caller può
 * derivare gli spell aggiunti senza scelta del giocatore.
 *
 * Per le grant "pick" ritorna il pool selezionabile dal giocatore (escluse
 * le spell già possedute).
 */
export function getSpellPoolForGrantInContext(
  grant: ActiveSpellGrant,
  ctx: SpellPoolContext,
): readonly Spell[] {
  const alreadyKnown = ctx.alreadyKnownSpellIds ?? EMPTY_SET;
  const characterLevel = ctx.characterLevel ?? 1;

  if (grant.moveId === CHIERICO_CASTING_MOVE) {
    return computePoolForChiericoLine(grant.grant, ctx.moves, ctx.archetypeId, alreadyKnown);
  }

  const ownClassId = resolveOwnClassId(grant.moveId, ctx.classId);
  return getSpellPoolForGrant(grant.grant, characterLevel, ownClassId, alreadyKnown);
}

/**
 * Risolve l'`ActiveSpellList` (mode + tradizione) della **linea Divina**
 * presente nel PG, partendo dalle sue mosse. Restituisce `undefined` se il
 * PG non possiede la mossa di lancio LIDV o nessuna mossa-padre Divina.
 *
 * Helper esposto per il caller della UI (es. `<SpellPickPanel>` header
 * dinamico in base a Tradizione/Druido).
 */
export function findChiericoLineActiveList(moves: readonly MoveRef[]): ActiveSpellList | undefined {
  for (const list of getActiveSpellLists(moves)) {
    if (
      list.mode === 'chierico-native' ||
      list.mode === 'druido-native' ||
      list.mode === 'paladino-native'
    ) {
      return list;
    }
  }
  return undefined;
}

function computePoolForChiericoLine(
  grant: SpellGrant,
  moves: readonly MoveRef[],
  archetypeId: string | undefined,
  alreadyKnown: ReadonlySet<string>,
): readonly Spell[] {
  const list = findChiericoLineActiveList(moves);
  if (!list) return [];

  const basePool = collectBasePoolForChiericoLineList(list);

  return basePool
    .filter((s) => matchesGrantLevelRule(s, grant.levelRule))
    .filter((s) => spellArchetypeOkForList(s, list, archetypeId))
    .filter((s) => !alreadyKnown.has(s.id));
}

function collectBasePoolForChiericoLineList(list: ActiveSpellList): readonly Spell[] {
  if (list.mode === 'chierico-native') {
    return findSpellsByClassAndTradition('chierico', list.tradizione);
  }
  if (list.mode === 'druido-native') {
    return findSpellsByClass('druido');
  }
  if (list.mode === 'paladino-native') {
    return findSpellsByClassAndTradition('paladino', list.tradizione);
  }
  return [];
}

function spellArchetypeOkForList(
  spell: Spell,
  list: ActiveSpellList,
  archetypeId: string | undefined,
): boolean {
  const access = spell.availableTo.find((a) => isAccessForList(a, list));
  if (!access) return false;
  return isSpellAccessAvailable(access, { archetypeId });
}

function isAccessForList(access: Spell['availableTo'][number], list: ActiveSpellList): boolean {
  if (access.classId !== list.classId) return false;
  if (list.mode === 'druido-native') return true;
  if (list.mode !== 'chierico-native' && list.mode !== 'paladino-native') return true;
  // narrow: list è chierico-native o paladino-native (entrambe hanno .tradizione)
  if (!('tradizione' in access)) return false;
  return (access as { tradizione: string }).tradizione === list.tradizione;
}

function matchesGrantLevelRule(spell: Spell, levelRule: SpellGrantLevelRule): boolean {
  switch (levelRule.kind) {
    case 'fixed':
    case 'all-of-level':
      return spell.level === levelRule.level;
    case 'lte-character-level':
      // Per la linea Divina la `lte-character-level` non è oggi usata (le grant
      // LIDV sono `all-of-level`). Lascio il branch per coerenza future-proof:
      // qualsiasi livello passa al filtro, il level filter viene poi applicato
      // dal caller via characterLevel (non disponibile qui).
      return true;
    case 'any-with-cap': {
      const cap = levelRule.maxLevel ?? 9;
      return spell.level <= cap;
    }
    case 'fixed-list':
      return levelRule.spellIds.includes(spell.id);
  }
}

function resolveOwnClassId(moveId: string, fallback: ClassId): ClassId {
  const prefix = moveId.split('.')[0];
  if (!prefix) return fallback;
  if (prefix in dwrClassesById) return prefix as ClassId;
  return fallback;
}

/**
 * Ricostruisce la lista di spell `auto` che la grant assegna al PG (no scelta
 * del giocatore). Wrapper specifico per le grant `all-of-level` / `fixed-list`
 * della linea Mago/Warlock/Chierico-Druido-Paladino, usato per popolare
 * `Character.spells` al momento del finalize / setup-L1.
 *
 * Per le grant "pick" ritorna `undefined` (le scelte vivono nello stato UI).
 */
export function getAutoSpellsForGrant(
  grant: ActiveSpellGrant,
  ctx: SpellPoolContext,
): readonly Spell[] | undefined {
  if (!isAutoSpellGrantLevelRule(grant.grant.levelRule)) return undefined;
  return getSpellPoolForGrantInContext(grant, ctx);
}

/**
 * Verifica se l'aggiunta di `spell` alla lista `selectedIds` violerebbe il
 * `perLevelCap` della grant. Helper esposto per il `<SpellPickPanel>` UI.
 */
export function isLevelCappedForGrant(
  spellLevel: number,
  selectedIds: readonly string[],
  perLevelCap: Readonly<Record<string, number>> | undefined,
): boolean {
  if (!perLevelCap) return false;
  const max = perLevelCap[String(spellLevel)];
  if (max === undefined) return false;
  const alreadyAtThisLevel = selectedIds
    .map((id) => findSpellById(id))
    .filter((s): s is NonNullable<typeof s> => s?.level === (spellLevel as SpellLevel)).length;
  return alreadyAtThisLevel >= max;
}

const EMPTY_SET: ReadonlySet<string> = new Set();
